import React, { useState, useEffect, useMemo } from 'react';
import {
    Edit3, Users, Activity, IdCard, GraduationCap, ShieldCheck, Check,
    ChevronRight, ChevronLeft, User, ClipboardList, Lock, Eye, X, Mail, Users2
} from 'lucide-react';
import './PeopleManager.css';
import API_BASE_URL from '../../config/api';

const getCleanEmail = (email) => {
    if (!email) return '';
    const atIdx = email.lastIndexOf('@');
    const localPart = atIdx > 0 ? email.substring(0, atIdx) : email;
    const domain = atIdx > 0 ? email.substring(atIdx) : '';
    const plusIdx = localPart.indexOf('+');
    return plusIdx > -1 ? localPart.substring(0, plusIdx) + domain : email;
};

const PersonWizardModal = ({ isOpen, userId = null, onClose, onSaved }) => {
    const [roles, setRoles] = useState([]);
    const [sports, setSports] = useState([]);
    const [teams, setTeams] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [validationErrors, setValidationErrors] = useState({});
    const [stepErrors, setStepErrors] = useState({});
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        birthDate: '',
        nif: '',
        address: '',
        postalCode: '',
        city: '',
        customQuotaPrice: '',
        hasAthleteProfile: false,
        hasMemberProfile: false,
        hasCoachProfile: false,
        hasCoordinatorProfile: false,
        athleteProfile: {
            id: null,
            firstName: '',
            lastName: '',
            escalao: '',
            teamId: ''
        },
        memberProfile: {
            membershipStatus: 0,
            memberSince: '',
            paymentPreference: ''
        },
        coachProfile: {
            sportId: '',
            teamIds: [],
            licenseNumber: '',
            licenseLevel: '',
            licenseExpiry: '',
            specialization: '',
            isAssistant: false
        },
        coordinatorProfile: {
            sportId: ''
        },
        selectedRoles: []
    });
    const [linkedMembers, setLinkedMembers] = useState([]);
    const [aliasConfirmModal, setAliasConfirmModal] = useState({ show: false, onConfirm: null });

    const relationships = ['Pai', 'Mãe', 'Filho(a)', 'Irmão/Irmã', 'Cônjuge', 'Outro'];

    // Wizard steps definition
    const steps = [
        { id: 'personal', label: 'Dados Pessoais', icon: <User size={18} />, description: 'Informação básica' },
        { id: 'profiles', label: 'Perfis', icon: <ClipboardList size={18} />, description: 'Tipo de pessoa' },
        { id: 'details', label: 'Detalhes', icon: <Eye size={18} />, description: 'Dados específicos' },
        { id: 'permissions', label: 'Permissões', icon: <Lock size={18} />, description: 'Acesso ao sistema' },
        { id: 'summary', label: 'Resumo', icon: <Check size={18} />, description: 'Confirmar dados' }
    ];

    // Check if details step should be shown
    const hasAthleteProfile = formData.hasAthleteProfile;
    const hasAnyProfile = hasAthleteProfile || formData.hasMemberProfile || formData.hasCoachProfile || formData.hasCoordinatorProfile;

    // Active steps (skip details if no profiles selected)
    const activeSteps = useMemo(() => {
        if (!hasAnyProfile) {
            return steps.filter(s => s.id !== 'details');
        }
        return steps;
    }, [hasAnyProfile]);

    const currentStepId = activeSteps[currentStep]?.id;
    const showModal = isOpen;

    useEffect(() => {
        if (!isOpen) return;
        resetForm();
        setLinkedMembers([]);
        if (userId) loadUser(userId);
    }, [isOpen, userId]);

    useEffect(() => {
        fetchRoles();
        fetchSports();
        fetchTeams();
    }, []);

    const handleClose = () => {
        resetForm();
        onClose();
    };

    // Auto-select "User" role when Athlete, Coach or Coordinator profile is selected
    useEffect(() => {
        if ((hasAthleteProfile || formData.hasCoachProfile || formData.hasCoordinatorProfile) && roles.length > 0) {
            const userRole = roles.find(role => role.name === 'User');
            if (userRole && !formData.selectedRoles.includes(userRole.id)) {
                setFormData(prev => ({
                    ...prev,
                    selectedRoles: [...prev.selectedRoles, userRole.id]
                }));
            }
        }
    }, [hasAthleteProfile, formData.hasCoachProfile, roles]);
    const fetchRoles = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/roles`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setRoles(data);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    const fetchSports = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/sports/all`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setSports(data.filter(s => s.isActive));
            }
        } catch (error) {
            console.error('Error fetching sports:', error);
        }
    };

    const fetchTeams = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/teams/all`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setTeams(data.filter(t => t.isActive));
            }
        } catch (error) {
            console.error('Error fetching teams:', error);
        }
    };
    const resetForm = () => {
        setFormData({
            email: '', firstName: '', lastName: '', phone: '', birthDate: '', nif: '',
            address: '', postalCode: '', city: '',
            customQuotaPrice: '',
            hasAthleteProfile: false,
            hasMemberProfile: false,
            hasCoachProfile: false,
            hasCoordinatorProfile: false,
            athleteProfile: { id: null, firstName: '', lastName: '', escalao: '', teamId: '' },
            memberProfile: { membershipStatus: 0, memberSince: '', paymentPreference: '' },
            coachProfile: { sportId: '', teamIds: [], licenseNumber: '', licenseLevel: '', licenseExpiry: '', specialization: '', isAssistant: false },
            coordinatorProfile: { sportId: '' },
            selectedRoles: []
        });
        setEditingUser(null);
        setCurrentStep(0);
        setValidationErrors({});
        setStepErrors({});
    };
    // Step-specific validation
    const validateStep = (stepId) => {
        const errors = {};

        if (stepId === 'personal') {
            if (!formData.email.trim()) errors.email = 'Email é obrigatório';
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Email inválido';
            if (!formData.firstName.trim()) errors.firstName = 'Nome é obrigatório';
            if (!formData.lastName.trim()) errors.lastName = 'Apelido é obrigatório';

            // Optional fields but with format validation if provided
            if (formData.nif && formData.nif.trim() && formData.nif.length !== 9) {
                errors.nif = 'NIF deve ter 9 dígitos';
            }
        }

        if (stepId === 'details') {
            if (formData.hasMemberProfile) {
                if (!formData.memberProfile.memberSince) errors.memberSince = 'Data de início é obrigatória';
                if (formData.memberProfile.membershipStatus === undefined || formData.memberProfile.membershipStatus === '') {
                    errors.membershipStatus = 'Estado é obrigatório';
                }
            }
            if (formData.hasCoachProfile) {
                if (!formData.coachProfile.sportId) errors.coachSport = 'Modalidade é obrigatória';
                if (!formData.coachProfile.licenseLevel && !formData.coachProfile.isAssistant) errors.coachLicenseLevel = 'Nível de cédula é obrigatório';
            }
            if (formData.hasCoordinatorProfile) {
                if (!formData.coordinatorProfile.sportId) errors.coordinatorSport = 'Modalidade é obrigatória';
            }
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateAllSteps = () => {
        let allErrors = {};

        // Validate personal
        if (!formData.email.trim()) allErrors.email = 'obrigatório';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) allErrors.email = 'inválido';
        if (!formData.firstName.trim()) allErrors.firstName = 'obrigatório';
        if (!formData.lastName.trim()) allErrors.lastName = 'obrigatório';

        // Optional fields validation
        if (formData.nif && formData.nif.trim() && formData.nif.length !== 9) {
            allErrors.nif = 'inválido';
        }

        // Validate details
        if (formData.hasMemberProfile) {
            if (!formData.memberProfile.memberSince) allErrors.memberSince = 'obrigatório';
        }
        if (formData.hasCoachProfile) {
            if (!formData.coachProfile.sportId) allErrors.coachSport = 'obrigatório';
            if (!formData.coachProfile.licenseLevel && !formData.coachProfile.isAssistant) allErrors.coachLicenseLevel = 'obrigatório';
        }
        if (formData.hasCoordinatorProfile) {
            if (!formData.coordinatorProfile.sportId) allErrors.coordinatorSport = 'obrigatório';
        }

        setValidationErrors(allErrors);
        return Object.keys(allErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(currentStepId)) {
            if (currentStep < activeSteps.length - 1) {
                setCurrentStep(currentStep + 1);
            }
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            setValidationErrors({});
        }
    };

    const goToStep = (index) => {
        // When editing, allow free navigation to any step
        if (editingUser) {
            setCurrentStep(index);
            setValidationErrors({});
            return;
        }
        // When creating, only allow going back to completed steps
        if (index < currentStep) {
            setCurrentStep(index);
            setValidationErrors({});
        }
    };
    const loadUser = async (id) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/users/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                // Strip +alias from email so admin sees the clean base email
                const baseEmail = getCleanEmail(data.email);
                setEditingUser(data);

                setFormData({
                    email: baseEmail,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    phone: data.phone || '',
                    birthDate: data.birthDate ? data.birthDate.split('T')[0] : '',
                    nif: data.nif || '',
                    address: data.address || '',
                    postalCode: data.postalCode || '',
                    city: data.city || '',
                    customQuotaPrice: data.customQuotaPrice || '',
                    hasAthleteProfile: data.hasAthleteProfile,
                    hasMemberProfile: data.hasMemberProfile,
                    hasCoachProfile: data.hasCoachProfile,
                    athleteProfile: data.athleteProfile ? {
                        id: data.athleteProfile.id,
                        firstName: data.athleteProfile.firstName || data.firstName || '',
                        lastName: data.athleteProfile.lastName || data.lastName || '',
                        escalao: data.athleteProfile.escalao || '',
                        teamId: data.athleteProfile.teams && data.athleteProfile.teams.length > 0 ? data.athleteProfile.teams[0].id : ''
                    } : { id: null, firstName: data.firstName || '', lastName: data.lastName || '', escalao: '', teamId: '' },
                    memberProfile: data.memberProfile ? {
                        membershipNumber: data.memberProfile.membershipNumber || '',
                        membershipStatus: data.memberProfile.membershipStatus,
                        memberSince: data.memberProfile.memberSince
                            ? data.memberProfile.memberSince.split('T')[0] : '',
                        paymentPreference: data.memberProfile.paymentPreference || '',
                        quotaPaidUntil: data.memberProfile.quotaPaidUntil
                            ? data.memberProfile.quotaPaidUntil.slice(0, 7) : ''
                    } : { membershipNumber: '', membershipStatus: 0, memberSince: '', paymentPreference: '' },
                    coachProfile: data.coachProfile ? {
                        sportId: data.coachProfile.sportId,
                        teamIds: data.coachProfile.teams ? data.coachProfile.teams.map(t => t.id) : [],
                        licenseNumber: data.coachProfile.licenseNumber || '',
                        licenseLevel: data.coachProfile.licenseLevel || '',
                        licenseExpiry: data.coachProfile.licenseExpiry
                            ? data.coachProfile.licenseExpiry.split('T')[0] : '',
                        specialization: data.coachProfile.specialization || '',
                        isAssistant: !!data.coachProfile.isAssistant
                    } : { sportId: '', teamIds: [], licenseNumber: '', licenseLevel: '', licenseExpiry: '', specialization: '', isAssistant: false },
                    hasCoordinatorProfile: data.hasCoordinatorProfile,
                    coordinatorProfile: data.coordinatorProfile ? {
                        sportId: data.coordinatorProfile.sportId
                    } : { sportId: '' },
                    selectedRoles: data.roles.map(r => r.id)
                });
            }
        } catch (error) {
            console.error('Error fetching user details:', error);
        }
        fetchLinkedMembers(id);
    };
    const fetchLinkedMembers = async (userId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/users/${userId}/all-linked-members`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setLinkedMembers(data);
            }
        } catch (error) {
            console.error('Error fetching linked members:', error);
        }
    };

    const handleUpdateReciprocalRelationship = async (otherUserId, linkId, relationship) => {
        try {
            const token = localStorage.getItem('token');
            const userId = editingUser.id;

            if (linkId === 0) {
                // It's an alias/new link — create explicit link
                const response = await fetch(`${API_BASE_URL}/api/users/${userId}/family-links`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ linkedUserId: otherUserId, relationship })
                });
                if (response.ok) fetchLinkedMembers(userId);
            } else {
                // Update existing link
                const response = await fetch(`${API_BASE_URL}/api/users/${userId}/family-links/${linkId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ relationship })
                });
                if (response.ok) fetchLinkedMembers(userId);
            }
        } catch (error) {
            console.error('Error updating relationship:', error);
        }
    };
    // Core submit logic — called after alias confirmation if needed
    // createFamilyLinkUserId: if set, creates an explicit family link with this user (used when keeping an implicit alias link)
    const doSubmit = async (finalEmail, removeFamilyLinkId = null, createFamilyLinkUserId = null) => {
        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            let userId = editingUser?.id;

            const userPayload = {
                email: finalEmail,
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone || null,
                birthDate: formData.birthDate || null,
                nif: formData.nif || null,
                address: formData.address || null,
                postalCode: formData.postalCode || null,
                city: formData.city || null,
                customQuotaPrice: formData.customQuotaPrice || null
            };

            if (editingUser) {
                const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify(userPayload)
                });
                if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    throw new Error(errData.message || 'Erro ao atualizar utilizador');
                }
            } else {
                const response = await fetch(`${API_BASE_URL}/api/users`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ ...userPayload, password: 'TempPassword123!' })
                });
                if (!response.ok) throw new Error('Erro ao criar utilizador');
                const data = await response.json();
                userId = data.id;
            }

            // Step 2: Manage Athlete Profile (single)
            if (formData.hasAthleteProfile) {
                const ap = formData.athleteProfile;
                const payload = {
                    firstName: ap.firstName || formData.firstName || null,
                    lastName: ap.lastName || formData.lastName || null,
                    escalao: ap.escalao || null,
                    height: null,
                    weight: null,
                    medicalCertificateExpiry: null,
                    teamId: ap.teamId ? parseInt(ap.teamId) : null
                };

                if (ap.id) {
                    // Existing profile — update
                    const res = await fetch(`${API_BASE_URL}/api/users/${userId}/athlete-profile`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify(payload)
                    });
                    if (!res.ok) {
                        const errData = await res.json().catch(() => ({}));
                        throw new Error(errData.message || 'Erro ao atualizar perfil de atleta');
                    }
                } else {
                    // New profile — create
                    const res = await fetch(`${API_BASE_URL}/api/users/${userId}/athlete-profile`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify(payload)
                    });
                    if (!res.ok) {
                        const errData = await res.json().catch(() => ({}));
                        throw new Error(errData.message || 'Erro ao criar perfil de atleta');
                    }
                }
            } else if (editingUser?.hasAthleteProfile) {
                // Profile was removed — deactivate it
                const delRes = await fetch(`${API_BASE_URL}/api/users/${userId}/athlete-profile`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!delRes.ok) {
                    const errData = await delRes.json().catch(() => ({}));
                    throw new Error(errData.message || 'Erro ao desativar perfil de atleta');
                }
            }

            // Step 3: Manage Member Profile
            if (formData.hasMemberProfile && !editingUser?.hasMemberProfile) {
                await fetch(`${API_BASE_URL}/api/users/${userId}/member-profile`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({
                        membershipStatus: parseInt(formData.memberProfile.membershipStatus),
                        memberSince: formData.memberProfile.memberSince || null,
                        paymentPreference: formData.memberProfile.paymentPreference || null,
                        quotaPaidUntil: formData.memberProfile.quotaPaidUntil ? `${formData.memberProfile.quotaPaidUntil}-01` : null
                    })
                });
            } else if (formData.hasMemberProfile && editingUser?.hasMemberProfile) {
                await fetch(`${API_BASE_URL}/api/users/${userId}/member-profile`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({
                        membershipNumber: formData.memberProfile.membershipNumber,
                        membershipStatus: parseInt(formData.memberProfile.membershipStatus),
                        memberSince: formData.memberProfile.memberSince || null,
                        paymentPreference: formData.memberProfile.paymentPreference || null,
                        quotaPaidUntil: formData.memberProfile.quotaPaidUntil ? `${formData.memberProfile.quotaPaidUntil}-01` : null
                    })
                });
            } else if (!formData.hasMemberProfile && editingUser?.hasMemberProfile) {
                await fetch(`${API_BASE_URL}/api/users/${userId}/member-profile`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            }

            // Step 4: Manage Coach Profile
            if (formData.hasCoachProfile && !editingUser?.hasCoachProfile && formData.coachProfile.sportId) {
                await fetch(`${API_BASE_URL}/api/users/${userId}/coach-profile`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({
                        sportId: parseInt(formData.coachProfile.sportId),
                        teamIds: formData.coachProfile.teamIds.map(tid => parseInt(tid)),
                        licenseNumber: formData.coachProfile.licenseNumber || null,
                        licenseLevel: formData.coachProfile.licenseLevel || null,
                        licenseExpiry: formData.coachProfile.licenseExpiry || null,
                        specialization: formData.coachProfile.specialization || null,
                        isAssistant: !!formData.coachProfile.isAssistant
                    })
                });
            } else if (formData.hasCoachProfile && editingUser?.hasCoachProfile && formData.coachProfile.sportId) {
                await fetch(`${API_BASE_URL}/api/users/${userId}/coach-profile`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({
                        sportId: parseInt(formData.coachProfile.sportId),
                        teamIds: formData.coachProfile.teamIds.map(tid => parseInt(tid)),
                        licenseNumber: formData.coachProfile.licenseNumber || null,
                        licenseLevel: formData.coachProfile.licenseLevel || null,
                        licenseExpiry: formData.coachProfile.licenseExpiry || null,
                        specialization: formData.coachProfile.specialization || null,
                        isAssistant: !!formData.coachProfile.isAssistant
                    })
                });
            } else if (!formData.hasCoachProfile && editingUser?.hasCoachProfile) {
                await fetch(`${API_BASE_URL}/api/users/${userId}/coach-profile`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            }

            // Step 4.5: Manage Coordinator Profile
            if (formData.hasCoordinatorProfile && !editingUser?.hasCoordinatorProfile && formData.coordinatorProfile.sportId) {
                await fetch(`${API_BASE_URL}/api/users/${userId}/coordinator-profile`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({
                        sportId: parseInt(formData.coordinatorProfile.sportId)
                    })
                });
            } else if (formData.hasCoordinatorProfile && editingUser?.hasCoordinatorProfile && formData.coordinatorProfile.sportId) {
                await fetch(`${API_BASE_URL}/api/users/${userId}/coordinator-profile`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({
                        sportId: parseInt(formData.coordinatorProfile.sportId)
                    })
                });
            } else if (!formData.hasCoordinatorProfile && editingUser?.hasCoordinatorProfile) {
                await fetch(`${API_BASE_URL}/api/users/${userId}/coordinator-profile`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            }

            // Step 5: Manage Roles
            if (editingUser) {
                const currentRoles = editingUser.roles.map(r => r.id);
                const newRoles = formData.selectedRoles;

                const rolesToAdd = newRoles.filter(r => !currentRoles.includes(r));
                for (const roleId of rolesToAdd) {
                    await fetch(`${API_BASE_URL}/api/users/${userId}/roles`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({ roleId })
                    });
                }

                const rolesToRemove = currentRoles.filter(r => !newRoles.includes(r));
                for (const roleId of rolesToRemove) {
                    await fetch(`${API_BASE_URL}/api/users/${userId}/roles/${roleId}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                }
            } else {
                for (const roleId of formData.selectedRoles) {
                    await fetch(`${API_BASE_URL}/api/users/${userId}/roles`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({ roleId })
                    });
                }
            }

            // If keeping an implicit alias link, create an explicit family link so it survives the email change
            if (createFamilyLinkUserId !== null) {
                await fetch(`${API_BASE_URL}/api/users/${userId}/family-links`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ linkedUserId: createFamilyLinkUserId, relationship: 'Outro' })
                });
            }

            // If removing an explicit family link (alias user chose to break association)
            if (removeFamilyLinkId !== null && removeFamilyLinkId > 0) {
                await fetch(`${API_BASE_URL}/api/users/${userId}/family-links/${removeFamilyLinkId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            }

            resetForm();
            if (onSaved) onSaved();
            onClose();
        } catch (error) {
            console.error('Error saving user:', error);
            alert('Erro ao guardar utilizador: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async () => {
        if (!validateAllSteps()) {
            alert('Existem erros de validação. Por favor verifique todos os passos.');
            return;
        }

        // Compute the final email to send
        let finalEmail = formData.email;
        let isAliasUser = false;
        let emailChanged = false;
        let aliasSuffix = ''; // e.g. '+joao'
        let baseOfOriginal = '';

        if (editingUser) {
            const originalEmail = editingUser.email || '';
            const atIdx = originalEmail.lastIndexOf('@');
            const localPart = atIdx > 0 ? originalEmail.substring(0, atIdx) : originalEmail;
            const domain = atIdx > 0 ? originalEmail.substring(atIdx) : '';
            const plusIdx = localPart.indexOf('+');

            aliasSuffix = plusIdx > -1 ? localPart.substring(plusIdx) : ''; // e.g. '+joao'
            baseOfOriginal = plusIdx > -1 ? localPart.substring(0, plusIdx) + domain : originalEmail;
            isAliasUser = plusIdx > -1;

            if (formData.email.toLowerCase() === baseOfOriginal.toLowerCase()) {
                // Email not changed — keep original aliased email
                finalEmail = originalEmail;
                emailChanged = false;
            } else {
                emailChanged = true;
                if (aliasSuffix) {
                    // Email changed — apply alias suffix to new base
                    const newAtIdx = formData.email.lastIndexOf('@');
                    const newLocal = newAtIdx > 0 ? formData.email.substring(0, newAtIdx) : formData.email;
                    const newDomain = newAtIdx > 0 ? formData.email.substring(newAtIdx) : '';
                    finalEmail = newLocal + aliasSuffix + newDomain;
                }
            }
        }

        // If this is an alias user AND the email changed AND they have family links, show custom confirmation
        if (isAliasUser && emailChanged && linkedMembers.length > 0) {
            // Find the linked member that represents the "original" user (the one without alias)
            // The original user is the one whose email == baseOfOriginal
            const originalLinkedMember = linkedMembers.find(lm => {
                const lmEmail = lm.email || '';
                const lmAtIdx = lmEmail.lastIndexOf('@');
                const lmLocal = lmAtIdx > 0 ? lmEmail.substring(0, lmAtIdx) : lmEmail;
                return lmLocal.indexOf('+') === -1 && lmEmail.toLowerCase() === baseOfOriginal.toLowerCase();
            });

            const originalName = originalLinkedMember?.fullName || baseOfOriginal;
            const linkId = originalLinkedMember?.linkId ?? 0;
            const capturedFinalEmail = finalEmail;

            setAliasConfirmModal({
                show: true,
                originalName,
                onKeep: () => {
                    setAliasConfirmModal({ show: false, onConfirm: null });
                    // If link was implicit (linkId=0), create an explicit link to survive the email change
                    // If link was already explicit (linkId>0), nothing to create — it stays
                    const createUserId = linkId === 0 ? (originalLinkedMember?.userId ?? null) : null;
                    doSubmit(capturedFinalEmail, null, createUserId);
                },
                onRemove: () => {
                    setAliasConfirmModal({ show: false, onConfirm: null });
                    // If link was explicit (linkId>0), delete it
                    // If link was implicit (linkId=0), nothing to delete — email change breaks the pattern
                    doSubmit(capturedFinalEmail, linkId > 0 ? linkId : null, null);
                }
            });
            return;
        }

        doSubmit(finalEmail);
    };
    // Helper to get membership status label
    const getMemberStatusLabel = (status) => {
        const s = parseInt(status);
        if (s === 0) return 'Pendente';
        if (s === 1) return 'Ativo';
        if (s === 2) return 'Inativo';
        return '-';
    };

    // Helper to get team name by id
    const getTeamName = (teamId) => {
        const team = teams.find(t => t.id === parseInt(teamId));
        return team ? team.name : '-';
    };

    // Helper to get sport name by id
    const getSportName = (sportId) => {
        const sport = sports.find(s => s.id === parseInt(sportId));
        return sport ? sport.name : '-';
    };
    const renderPersonalStep = () => (
        <div className="wizard-step-content">
            <div className="step-intro">
                <h3>Informação Pessoal</h3>
                <p>Preencha os dados pessoais do utilizador. Campos com * são obrigatórios.</p>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label>Email *</label>
                    <input
                        type="email"
                        placeholder="exemplo@email.com"
                        className={validationErrors.email ? 'error' : ''}
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                    {validationErrors.email && <span className="error-message">{validationErrors.email}</span>}
                </div>
                <div className="form-group">
                    <label>NIF</label>
                    <input
                        type="text"
                        maxLength="9"
                        placeholder="123456789"
                        className={validationErrors.nif ? 'error' : ''}
                        value={formData.nif}
                        onChange={(e) => setFormData({ ...formData, nif: e.target.value.replace(/\D/g, '') })}
                    />
                    {validationErrors.nif && <span className="error-message">{validationErrors.nif}</span>}
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label>Primeiro Nome *</label>
                    <input
                        type="text"
                        placeholder="João"
                        className={validationErrors.firstName ? 'error' : ''}
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    />
                    {validationErrors.firstName && <span className="error-message">{validationErrors.firstName}</span>}
                </div>
                <div className="form-group">
                    <label>Apelido *</label>
                    <input
                        type="text"
                        placeholder="Silva"
                        className={validationErrors.lastName ? 'error' : ''}
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    />
                    {validationErrors.lastName && <span className="error-message">{validationErrors.lastName}</span>}
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label>Telefone</label>
                    <input
                        type="tel"
                        placeholder="912345678"
                        className={validationErrors.phone ? 'error' : ''}
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                    {validationErrors.phone && <span className="error-message">{validationErrors.phone}</span>}
                </div>
                <div className="form-group">
                    <label>Data de Nascimento</label>
                    <input
                        type="date"
                        className={validationErrors.birthDate ? 'error' : ''}
                        value={formData.birthDate}
                        onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                    />
                    {validationErrors.birthDate && <span className="error-message">{validationErrors.birthDate}</span>}
                </div>
            </div>

            <div className="form-divider">
                <span>Morada</span>
            </div>

            <div className="form-row">
                <div className="form-group full-width">
                    <label>Morada</label>
                    <input
                        type="text"
                        placeholder="Rua Exemplo, nº 123"
                        className={validationErrors.address ? 'error' : ''}
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    />
                    {validationErrors.address && <span className="error-message">{validationErrors.address}</span>}
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label>Código Postal</label>
                    <input
                        type="text"
                        placeholder="0000-000"
                        className={validationErrors.postalCode ? 'error' : ''}
                        value={formData.postalCode}
                        onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    />
                    {validationErrors.postalCode && <span className="error-message">{validationErrors.postalCode}</span>}
                </div>
                <div className="form-group">
                    <label>Cidade</label>
                    <input
                        type="text"
                        placeholder="Póvoa de Varzim"
                        className={validationErrors.city ? 'error' : ''}
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                    {validationErrors.city && <span className="error-message">{validationErrors.city}</span>}
                </div>
            </div>

            {editingUser && linkedMembers.length > 0 && (
                <div className="form-divider" style={{ marginTop: '32px' }}>
                    <span>Contas Associadas e Parentesco</span>
                </div>
            )}

            {editingUser && linkedMembers.length > 0 && (
                <div className="linked-members-management">
                    <p className="step-help-text">Estes utilizadores partilham o mesmo email base ou foram associados manualmente.</p>
                    <div className="linked-members-list">
                        {linkedMembers.map(member => (
                            <div key={member.userId} className="linked-member-card">
                                <div className="member-main-info">
                                    <div className="member-avatar">
                                        {member.fullName.charAt(0)}
                                    </div>
                                    <div className="member-text">
                                        <div className="member-name">{member.fullName}</div>
                                        <div className="member-email">{getCleanEmail(member.email)}</div>
                                    </div>
                                </div>
                                <div className="member-rel-select">
                                    <label>Parentesco:</label>
                                    <select
                                        value={member.relationship || 'Outro'}
                                        onChange={(e) => handleUpdateReciprocalRelationship(member.userId, member.linkId, e.target.value)}
                                    >
                                        {relationships.map(r => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    const renderProfilesStep = () => (
        <div className="wizard-step-content">
            <div className="step-intro">
                <h3>Que tipo de pessoa é?</h3>
                <p>Selecione um ou mais perfis para este utilizador. Pode sempre alterar mais tarde.</p>
            </div>

            <div className="profile-cards-grid">
                {/* Athlete Card */}
                <div
                    className={`profile-card ${hasAthleteProfile ? 'selected' : ''}`}
                    onClick={() => {
                        if (hasAthleteProfile) {
                            // Deselect: clear athlete profile
                            setFormData({
                                ...formData,
                                hasAthleteProfile: false,
                                athleteProfile: { id: null, firstName: '', lastName: '', teamId: '' },
                                hasMemberProfile: formData.hasMemberProfile
                            });
                        } else {
                            // Select: enable athlete profile
                            setFormData({
                                ...formData,
                                hasAthleteProfile: true,
                                hasMemberProfile: true
                            });
                        }
                    }}
                >
                    <div className="profile-card-icon athlete">
                        <Activity />
                    </div>
                    <div className="profile-card-info">
                        <h4>Atleta</h4>
                        <p>Pessoa que pratica desporto federado no clube</p>
                    </div>
                    <div className="profile-card-check">
                        {hasAthleteProfile && <Check />}
                    </div>
                </div>

                {/* Member Card */}
                <div
                    className={`profile-card ${formData.hasMemberProfile ? 'selected' : ''} ${hasAthleteProfile ? 'locked' : ''}`}
                    onClick={() => {
                        if (!hasAthleteProfile) {
                            setFormData({ ...formData, hasMemberProfile: !formData.hasMemberProfile });
                        }
                    }}
                >
                    <div className="profile-card-icon member">
                        <IdCard />
                    </div>
                    <div className="profile-card-info">
                        <h4>Sócio</h4>
                        <p>Membro associado do clube com quotas</p>
                        {hasAthleteProfile && (
                            <span className="auto-tag">Automático — todos os atletas são sócios</span>
                        )}
                    </div>
                    <div className="profile-card-check">
                        {formData.hasMemberProfile && <Check />}
                    </div>
                </div>

                {/* Coach Card */}
                <div
                    className={`profile-card ${formData.hasCoachProfile ? 'selected' : ''}`}
                    onClick={() => setFormData({ ...formData, hasCoachProfile: !formData.hasCoachProfile })}
                >
                    <div className="profile-card-icon coach">
                        <GraduationCap />
                    </div>
                    <div className="profile-card-info">
                        <h4>Treinador</h4>
                        <p>Responsável técnico de uma ou mais equipas</p>
                    </div>
                    <div className="profile-card-check">
                        {formData.hasCoachProfile && <Check />}
                    </div>
                </div>

                {/* Coordinator Card */}
                <div
                    className={`profile-card ${formData.hasCoordinatorProfile ? 'selected' : ''}`}
                    onClick={() => setFormData({ ...formData, hasCoordinatorProfile: !formData.hasCoordinatorProfile })}
                >
                    <div className="profile-card-icon coordinator">
                        <Users />
                    </div>
                    <div className="profile-card-info">
                        <h4>Coordenador</h4>
                        <p>Responsável pela coordenação de uma modalidade</p>
                    </div>
                    <div className="profile-card-check">
                        {formData.hasCoordinatorProfile && <Check />}
                    </div>
                </div>
            </div>

            {!hasAnyProfile && (
                <div className="profile-hint">
                    <span>Sem perfis selecionados?</span> A pessoa será criada apenas com dados pessoais. Pode adicionar perfis mais tarde.
                </div>
            )}
        </div>
    );

    const renderDetailsStep = () => (
        <div className="wizard-step-content">
            <div className="step-intro">
                <h3>Detalhes dos Perfis</h3>
                <p>Complete a informação específica de cada perfil selecionado.</p>
            </div>

            {/* Athlete Details - Single Profile */}
            {hasAthleteProfile && (
                <div className="detail-section-body">
                    <div className="form-row">
                        <div className="form-group">
                            <label>Escalão Socioeconómico</label>
                            <select
                                value={formData.athleteProfile.escalao}
                                onChange={(e) => setFormData({ ...formData, athleteProfile: { ...formData.athleteProfile, escalao: e.target.value } })}
                            >
                                <option value="">Sem Escalão</option>
                                <option value="Escalão 1">Escalão 1</option>
                                <option value="Escalão 2">Escalão 2</option>
                            </select>
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Equipa</label>
                            <select
                                value={formData.athleteProfile.teamId}
                                onChange={(e) => setFormData({ ...formData, athleteProfile: { ...formData.athleteProfile, teamId: e.target.value } })}
                            >
                                <option value="">Sem equipa atribuída</option>
                                {teams.map(team => (
                                    <option key={team.id} value={team.id}>
                                        {team.name} ({team.sportName})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* Member Details */}
            {
                formData.hasMemberProfile && (
                    <div className="detail-section">
                        <div className="detail-section-header">
                            <div className="detail-section-icon member"><IdCard /></div>
                            <h4>Dados de Sócio</h4>
                        </div>
                        <div className="detail-section-body">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Estado *</label>
                                    <select
                                        className={validationErrors.membershipStatus ? 'error' : ''}
                                        value={formData.memberProfile.membershipStatus}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            memberProfile: { ...formData.memberProfile, membershipStatus: e.target.value }
                                        })}
                                    >
                                        <option value="0">Pendente</option>
                                        <option value="1">Ativo</option>
                                        <option value="2">Inativo</option>
                                    </select>
                                    {validationErrors.membershipStatus && <span className="error-message">{validationErrors.membershipStatus}</span>}
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Sócio Desde *</label>
                                    <input
                                        type="date"
                                        className={validationErrors.memberSince ? 'error' : ''}
                                        value={formData.memberProfile.memberSince}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            memberProfile: { ...formData.memberProfile, memberSince: e.target.value }
                                        })}
                                    />
                                    {validationErrors.memberSince && <span className="error-message">{validationErrors.memberSince}</span>}
                                </div>
                                <div className="form-group">
                                    <label>Preferência de Pagamento</label>
                                    <select
                                        value={formData.memberProfile.paymentPreference}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            memberProfile: { ...formData.memberProfile, paymentPreference: e.target.value }
                                        })}
                                    >
                                        <option value="">Selecione...</option>
                                        <option value="Monthly">Mensal</option>
                                        <option value="Annual">Anual</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Preço Customizado de Quota (€)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        placeholder="Ex: 5.00 (deixe vazio para automático)"
                                        value={formData.customQuotaPrice || ''}
                                        onChange={(e) => setFormData({ ...formData, customQuotaPrice: e.target.value })}
                                    />
                                    <small style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                                        Se definido, ignora o cálculo automático.
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Coach Details */}
            {
                formData.hasCoachProfile && (
                    <div className="detail-section">
                        <div className="detail-section-header">
                            <div className="detail-section-icon coach"><GraduationCap /></div>
                            <h4>Dados de Treinador</h4>
                        </div>
                        <div className="detail-section-body">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Modalidade *</label>
                                    <select
                                        className={validationErrors.coachSport ? 'error' : ''}
                                        value={formData.coachProfile.sportId}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            coachProfile: { ...formData.coachProfile, sportId: e.target.value }
                                        })}
                                    >
                                        <option value="">Selecione a modalidade...</option>
                                        {sports.map(sport => (
                                            <option key={sport.id} value={sport.id}>{sport.name}</option>
                                        ))}
                                    </select>
                                    {validationErrors.coachSport && <span className="error-message">{validationErrors.coachSport}</span>}
                                </div>
                                <div className="form-group full-width">
                                    <label>Equipas</label>
                                    <div className="multi-select-teams">
                                        {teams
                                            .filter(t => !formData.coachProfile.sportId || t.sportId === parseInt(formData.coachProfile.sportId))
                                            .map(team => (
                                                <label key={team.id} className="team-checkbox-item">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.coachProfile.teamIds.includes(team.id)}
                                                        onChange={(e) => {
                                                            const newTeamIds = e.target.checked
                                                                ? [...formData.coachProfile.teamIds, team.id]
                                                                : formData.coachProfile.teamIds.filter(id => id !== team.id);
                                                            setFormData({
                                                                ...formData,
                                                                coachProfile: { ...formData.coachProfile, teamIds: newTeamIds }
                                                            });
                                                        }}
                                                    />
                                                    <span className="team-name">{team.name}</span>
                                                </label>
                                            ))}
                                        {teams.filter(t => !formData.coachProfile.sportId || t.sportId === parseInt(formData.coachProfile.sportId)).length === 0 && (
                                            <p className="no-teams-hint">Selecione uma modalidade para ver as equipas disponíveis.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group full-width">
                                    <label className="team-checkbox-item">
                                        <input
                                            type="checkbox"
                                            checked={!!formData.coachProfile.isAssistant}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                coachProfile: { ...formData.coachProfile, isAssistant: e.target.checked }
                                            })}
                                        />
                                        <span className="team-name">Treinador adjunto</span>
                                    </label>
                                    {formData.coachProfile.isAssistant && formData.memberProfile.membershipStatus !== 'Active' && formData.memberProfile.membershipStatus !== 1 && (
                                        <span className="error-message">Só sócios com quotas ativas podem ser adjuntos.</span>
                                    )}
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Nº Cédula</label>
                                    <input
                                        type="text"
                                        placeholder="Número da cédula"
                                        value={formData.coachProfile.licenseNumber}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            coachProfile: { ...formData.coachProfile, licenseNumber: e.target.value }
                                        })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Nível de Cédula {formData.coachProfile.isAssistant ? '' : '*'}</label>
                                    <input
                                        type="text"
                                        placeholder="Nível 1, Nível 2, etc."
                                        className={validationErrors.coachLicenseLevel ? 'error' : ''}
                                        value={formData.coachProfile.licenseLevel}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            coachProfile: { ...formData.coachProfile, licenseLevel: e.target.value }
                                        })}
                                    />
                                    {validationErrors.coachLicenseLevel && <span className="error-message">{validationErrors.coachLicenseLevel}</span>}
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Validade Cédula</label>
                                    <input
                                        type="date"
                                        value={formData.coachProfile.licenseExpiry}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            coachProfile: { ...formData.coachProfile, licenseExpiry: e.target.value }
                                        })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Especialização</label>
                                    <input
                                        type="text"
                                        placeholder="Ex: Formação, Competição..."
                                        value={formData.coachProfile.specialization}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            coachProfile: { ...formData.coachProfile, specialization: e.target.value }
                                        })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Coordinator Details */}
            {
                formData.hasCoordinatorProfile && (
                    <div className="detail-section">
                        <div className="detail-section-header">
                            <div className="detail-section-icon coordinator"><Users /></div>
                            <h4>Dados de Coordenador</h4>
                        </div>
                        <div className="detail-section-body">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Modalidade *</label>
                                    <select
                                        className={validationErrors.coordinatorSport ? 'error' : ''}
                                        value={formData.coordinatorProfile.sportId}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            coordinatorProfile: { ...formData.coordinatorProfile, sportId: e.target.value }
                                        })}
                                    >
                                        <option value="">Selecione a modalidade...</option>
                                        {sports.map(sport => (
                                            <option key={sport.id} value={sport.id}>{sport.name}</option>
                                        ))}
                                    </select>
                                    {validationErrors.coordinatorSport && <span className="error-message">{validationErrors.coordinatorSport}</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );

    const renderPermissionsStep = () => (
        <div className="wizard-step-content">
            <div className="step-intro">
                <h3>Permissões de Acesso</h3>
                <p>Defina que áreas do sistema este utilizador pode aceder. Pode deixar vazio se não precisar de acesso especial.</p>
            </div>

            <div className="roles-grid">
                {roles.map(role => (
                    <label key={role.id} className={`role-card ${formData.selectedRoles.includes(role.id) ? 'selected' : ''}`}>
                        <input
                            type="checkbox"
                            checked={formData.selectedRoles.includes(role.id)}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setFormData({ ...formData, selectedRoles: [...formData.selectedRoles, role.id] });
                                } else {
                                    setFormData({ ...formData, selectedRoles: formData.selectedRoles.filter(id => id !== role.id) });
                                }
                            }}
                        />
                        <div className="role-card-content">
                            <ShieldCheck className="role-card-icon" />
                            <div>
                                <strong>{role.name}</strong>
                                {role.description && <p>{role.description}</p>}
                            </div>
                        </div>
                    </label>
                ))}
            </div>

            {formData.selectedRoles.length === 0 && (
                <div className="profile-hint">
                    Sem permissões especiais selecionadas. O utilizador terá acesso básico ao sistema.
                </div>
            )}
        </div>
    );

    const renderSummaryStep = () => (
        <div className="wizard-step-content">
            <div className="step-intro">
                <h3>Confirmar Dados</h3>
                <p>Reveja toda a informação antes de {editingUser ? 'atualizar' : 'criar'} o utilizador.</p>
            </div>

            <div className="summary-grid">
                {/* Personal Summary */}
                <div className="summary-card">
                    <div className="summary-card-header">
                        <User />
                        <h4>Dados Pessoais</h4>
                        <button type="button" className="summary-edit-btn" onClick={() => setCurrentStep(0)}>
                            <Edit3 size={16} /> Editar
                        </button>
                    </div>
                    <div className="summary-card-body">
                        <div className="summary-row">
                            <span className="summary-label">Nome</span>
                            <span className="summary-value">{formData.firstName} {formData.lastName}</span>
                        </div>
                        <div className="summary-row">
                            <span className="summary-label">Email</span>
                            <span className="summary-value">{formData.email}</span>
                        </div>
                        <div className="summary-row">
                            <span className="summary-label">Telefone</span>
                            <span className="summary-value">{formData.phone || '-'}</span>
                        </div>
                        <div className="summary-row">
                            <span className="summary-label">NIF</span>
                            <span className="summary-value">{formData.nif}</span>
                        </div>
                        <div className="summary-row">
                            <span className="summary-label">Data Nasc.</span>
                            <span className="summary-value">{formData.birthDate || '-'}</span>
                        </div>
                        <div className="summary-row">
                            <span className="summary-label">Morada</span>
                            <span className="summary-value">{formData.address}, {formData.postalCode} {formData.city}</span>
                        </div>
                    </div>
                </div>

                {/* Profiles Summary */}
                <div className="summary-card">
                    <div className="summary-card-header">
                        <ClipboardList />
                        <h4>Perfis</h4>
                        <button type="button" className="summary-edit-btn" onClick={() => setCurrentStep(activeSteps.findIndex(s => s.id === 'profiles'))}>
                            <Edit3 size={16} /> Editar
                        </button>
                    </div>
                    <div className="summary-card-body">
                        {!hasAnyProfile && (
                            <div className="summary-row">
                                <span className="summary-value" style={{ color: '#6b7280' }}>Sem perfis atribuídos</span>
                            </div>
                        )}
                        {hasAthleteProfile && (
                            <div className="summary-profile-block">
                                <span className="profile-badge athlete"><Activity /> Atleta</span>
                                <div className="summary-details">
                                    <span>
                                        {formData.athleteProfile.firstName || formData.athleteProfile.lastName
                                            ? `${formData.athleteProfile.firstName} ${formData.athleteProfile.lastName}`.trim()
                                            : 'Nome não definido'}
                                        {formData.athleteProfile.teamId ? ` — ${getTeamName(formData.athleteProfile.teamId)}` : ''}
                                    </span>
                                </div>
                            </div>
                        )}
                        {formData.hasMemberProfile && (
                            <div className="summary-profile-block">
                                <span className="profile-badge member"><IdCard /> Sócio</span>
                                <div className="summary-details">
                                    <span>Estado: {getMemberStatusLabel(formData.memberProfile.membershipStatus)}</span>
                                    {formData.memberProfile.memberSince && <span>Desde: {formData.memberProfile.memberSince}</span>}
                                    {formData.customQuotaPrice && (
                                        <div style={{ marginTop: '4px', fontWeight: 'bold', color: '#059669' }}>
                                            <span>Quota Personalizada: {formData.customQuotaPrice}€</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {formData.hasCoachProfile && (
                            <div className="summary-profile-block">
                                <span className="profile-badge coach"><GraduationCap /> Treinador</span>
                                <div className="summary-details">
                                    {formData.coachProfile.sportId && <span>Modalidade: {getSportName(formData.coachProfile.sportId)}</span>}
                                    {formData.coachProfile.licenseLevel && <span>Cédula: {formData.coachProfile.licenseLevel}</span>}
                                </div>
                            </div>
                        )}
                        {formData.hasCoordinatorProfile && (
                            <div className="summary-profile-block">
                                <span className="profile-badge coordinator"><Users /> Coordenador</span>
                                <div className="summary-details">
                                    {formData.coordinatorProfile.sportId && <span>Modalidade: {getSportName(formData.coordinatorProfile.sportId)}</span>}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Permissions Summary */}
                <div className="summary-card">
                    <div className="summary-card-header">
                        <ShieldCheck size={20} />
                        <h4>Permissões e Papéis</h4>
                        <button type="button" className="summary-edit-btn" onClick={() => setCurrentStep(activeSteps.findIndex(s => s.id === 'permissions'))}>
                            <Edit3 size={16} /> Editar
                        </button>
                    </div>
                    <div className="summary-card-body">
                        {formData.selectedRoles.length === 0 ? (
                            <div className="summary-row">
                                <span className="summary-value" style={{ color: '#6b7280' }}>Acesso básico (sem permissões especiais)</span>
                            </div>
                        ) : (
                            <div className="summary-roles">
                                {formData.selectedRoles.map(roleId => {
                                    const role = roles.find(r => r.id === roleId);
                                    return role ? (
                                        <span key={roleId} className="role-badge"><ShieldCheck /> {role.name}</span>
                                    ) : null;
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderStepContent = () => {
        switch (currentStepId) {
            case 'personal': return renderPersonalStep();
            case 'profiles': return renderProfilesStep();
            case 'details': return renderDetailsStep();
            case 'permissions': return renderPermissionsStep();
            case 'summary': return renderSummaryStep();
            default: return null;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="people-manager" style={{ padding: 0, margin: 0, maxWidth: 'none' }}>
            {/* Wizard Modal */}
            {showModal && (
                <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) { handleClose(); } }}>
                    <div className="modal-content wizard-modal" onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="modal-header">
                            <h2>{editingUser ? 'Editar Pessoa' : 'Adicionar Pessoa'}</h2>
                            <button className="close-btn" onClick={() => { handleClose(); }}>
                                <X />
                            </button>
                        </div>

                        {/* Wizard Stepper */}
                        <div className="wizard-stepper">
                            <div className="wizard-progress-bar">
                                <div
                                    className="wizard-progress-fill"
                                    style={{ width: `${(currentStep / (activeSteps.length - 1)) * 100}%` }}
                                />
                            </div>
                            <div className="wizard-steps">
                                {activeSteps.map((step, index) => (
                                    <div
                                        key={step.id}
                                        className={`wizard-step ${index === currentStep ? 'active' : ''} ${index < currentStep || editingUser ? 'completed' : ''}`}
                                        onClick={() => goToStep(index)}
                                    >
                                        <div className="wizard-step-number">
                                            {(index < currentStep || (editingUser && index !== currentStep)) ? <Check /> : index + 1}
                                        </div>
                                        <div className="wizard-step-label">
                                            <span className="wizard-step-title">{step.label}</span>
                                            <span className="wizard-step-desc">{step.description}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Step Content */}
                        <div className="modal-body">
                            <div key={currentStepId} className="wizard-step-content">
                                {renderStepContent()}
                            </div>
                        </div>

                        {/* Footer with navigation */}
                        <div className="modal-footer">
                            <div className="footer-left">
                                {currentStep > 0 && (
                                    <button type="button" className="btn-back" onClick={handleBack}>
                                        <ChevronLeft /> Voltar
                                    </button>
                                )}
                            </div>
                            <div className="footer-right">
                                <button
                                    type="button"
                                    className="btn-cancel"
                                    onClick={() => { handleClose(); }}
                                >
                                    Cancelar
                                </button>

                                {currentStepId === 'summary' ? (
                                    <button
                                        type="button"
                                        className="btn-submit"
                                        disabled={submitting}
                                        onClick={handleSubmit}
                                    >
                                        {submitting ? (
                                            <><div className="spinner-small"></div> A guardar...</>
                                        ) : (
                                            <><Check /> {editingUser ? 'Atualizar Pessoa' : 'Criar Pessoa'}</>
                                        )}
                                    </button>
                                ) : (
                                    <button type="button" className="btn-next" onClick={handleNext}>
                                        Seguinte <ChevronRight />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

            )}

            {/* Alias Email Change Confirmation Modal */}
            {aliasConfirmModal.show && (
                <div className="modal-overlay alias-confirm-overlay">
                    <div className="alias-confirm-modal">
                        <div className="alias-confirm-icon">
                            <Mail />
                        </div>
                        <h3>Alterar Email</h3>
                        <p>
                            Tem a certeza que quer alterar o email deste utilizador?<br />
                            <strong>Quer manter a associação familiar com {aliasConfirmModal.originalName}?</strong>
                        </p>
                        <div className="alias-confirm-actions">
                            <button
                                className="alias-confirm-btn alias-confirm-keep"
                                onClick={aliasConfirmModal.onKeep}
                                disabled={submitting}
                            >
                                <Users2 /> Sim, manter associação
                            </button>
                            <button
                                className="alias-confirm-btn alias-confirm-remove"
                                onClick={aliasConfirmModal.onRemove}
                                disabled={submitting}
                            >
                                <X /> Não, remover associação
                            </button>
                        </div>
                        <button
                            className="alias-confirm-cancel"
                            onClick={() => setAliasConfirmModal({ show: false, onConfirm: null })}
                            disabled={submitting}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PersonWizardModal;